import { io } from 'socket.io-client'
import { getDeviceId } from './device'
import { decode } from 'js-base64'
import { REGION } from '@ms/config/app'

/** 正则通配符
 * bingoWin^boughtAmount^37^~  //末位
 * bingoWin^~^37  // 中间
 * 不适用与开头就是通配符的，需要调整下面规则
 * */
const REGEXP_MAP = {
  '^~': '^[^^]+'
}

const regExpKeys = Object.keys(REGEXP_MAP)

// REGEXP_MAP中的key包含的char，用于将REGEXP_MAP中的key转换为regexp进行全局替换
const constRegexpKey = /(\^|~)/g

const uuid = (function () {
  let uid = 0
  return function () {
    return ++uid
  }
})()

export default class PushCenter {
  /**
   * multi 推送：向相同 accountId 的设备进行推送，当有多个在线时全部收到，当不在线时进行消息缓存，当任意一个上线拿走消息。
   * special 推送：向指定 deviceId 的设备推送，全局唯一，支持缓存。
   * group 推送：向 topic 内所有进行推送，不支持缓存。
   */
  PUSH_TYPE = {
    MULTI: 'MULTI',
    GROUP: 'GROUP',
    SPECIAL: 'SPECIAL'
  }

  // 分割符
  SPLIT_CHAR = '@@'
  // socket 实例
  socket = null
  // 请求的回调map
  requestCallbacks = {}
  // 话题的订阅者
  topicListeners = {}
  // 是否就绪
  isReady = false
  // 通配符方式
  topicRegListeners = {}
  // 通配符方式：topic 与regexp的对应关系
  topicRegMap = {}

  // 后台告知报错，需上锁进行处理
  serverErrorLock = false

  /**
   * 构造函数
   * @param  {String} options.devType       类型
   * @param  {String|Number} options.productCode   产品 code
   * @param  {String} options.deviceId      设备 id
   * @param  {String} options.accountId     账户
   * @param  {[type]} options.server        推送服务地址
   * @param  {Object} options.socketOptions io 配置
   * @return {[type]}                       [description]
   */
  constructor({ devType = 'WEB', productCode = '', deviceId = '', accountId = '', server = '', socketOptions = {} } = {}) {
    // 设备id
    if (!deviceId) {
      deviceId = getDeviceId()
    }

    socketOptions.transports = ['websocket', 'polling']
    socketOptions.upgrade = false

    const socketServer = process.env.VUE_APP_SOCKET.replace('xx', REGION)

    this.config = {
      devType,
      productCode,
      deviceId,
      accountId,
      server: server || socketServer,
      socketOptions
    }
  }

  // 更新 config 参数
  updateConfig(options = {}) {
    for (const key in options) {
      if (key in this.config) {
        this.config[key] = options[key]
      }
    }
  }

  init() {
    if (this.tryConnected) return
    this._connect()
    this.tryConnected = true
  }

  /**
   * 获取 listener key, 处理不含有通配符的topic
   * @param  {String} topic    topic
   * @param  {String} pushType 推送类型
   * @return {String}          listener key
   */
  _getListenerKey(topic = '', pushType = '') {
    return `${topic}${this.SPLIT_CHAR}${pushType}`
  }

  _connect() {
    const { server, socketOptions } = this.config
    this.socket = io(server, socketOptions)
    this.socket.on('connect', () => {
      // 这里也可能是 reconnect
      // 建立连接后立马注册
      this._register(() => {
        const { SPLIT_CHAR } = this
        // ready 置为 true，后续的新 sub 可以直接发 subRequest
        this.isReady = true
        // disconnect之后，之前sub的话题服务器都会不认，
        // 需要重新订阅
        for (const listenerKey in this.topicListeners) {
          if (this.topicListeners[listenerKey] && this.topicListeners[listenerKey].length) {
            const keys = listenerKey.split(SPLIT_CHAR)
            // 主题
            const topic = keys[0]
            // 推送类型
            const pushType = keys[1]
            this._postSubRequest(topic, pushType)
          }
        }

        // 通配符方式的topic
        for (const listenerRegKey in this.topicRegListeners) {
          if (this.topicRegListeners[listenerRegKey] && this.topicRegListeners[listenerRegKey].length) {
            const keys = listenerRegKey.split(SPLIT_CHAR)
            // 主题
            const topic = keys[0]
            // 推送类型
            const pushType = keys[1]
            this._postSubRequest(topic, pushType)
          }
        }
      })
    })
    this.socket.on('disconnect', () => {
      this.isReady = false
    })
    // 收到请求开始处理
    this.socket.on('data', (data) => {
      this._parseResponse(data)
    })
    this.socket.on('connect_error', (error) => {
      console.error(error)
    })
    this.socket.io.on('error', (error) => {
      console.error(error)
    })
  }

  // 向数据平台注册
  _register(callback) {
    const data = {
      devType: this.config.devType,
      deviceId: this.config.deviceId
    }
    this._postRequest(data, 'reg', callback)
  }

  /**
   * 发送请求，用作内部调用，不作为暴露的API
   */
  _postRequest(data = {}, type, callback) {
    const { productCode } = this.config
    const requestId = uuid()
    if (typeof callback === 'function') {
      this.requestCallbacks[requestId] = callback // 保存回调函数
    }
    data.requestId = requestId
    data.productCode = productCode
    this.socket.emit('data', {
      type,
      data: JSON.stringify(data)
    })
  }

  // 订阅主题请求
  _postSubRequest(topic, pushType, callback) {
    const data = {
      topic,
      subType: 'SUB',
      pushType
    }
    // multi push
    if (this.config.accountId && pushType === this.PUSH_TYPE.MULTI) {
      data.accountId = this.config.accountId
    }
    this._postRequest(data, 'sub', callback)
  }

  // 发出取消订阅的请求
  _postUnsubRequest(topic, pushType, callback) {
    const data = {
      topic,
      subType: 'UNSUB',
      pushType
    }
    // multi push
    if (this.config.accountId && pushType === this.PUSH_TYPE.MULTI) {
      data.accountId = this.config.accountId
    }
    this._postRequest(data, 'sub', callback)
  }

  // 响应socket接受到的数据
  _parseResponse(res = {}) {
    const data = JSON.parse(res.data || '{}')
    if (res.type === 'resp') {
      if (data.rightHost === '422') {
        // 后台数据解析错误
        if (!this.serverErrorLock) {
          alert("Sorry, the real-time data update can't work well in your current Firefox browser. Please enjoy MSport under another browser (Google Chrome is recommended).")
          this.serverErrorLock = true
        }
      } else {
        const { requestCallbacks } = this
        const { requestId } = data
        const callback = requestCallbacks[requestId]
        if (callback) {
          callback.call(this, data)
          delete requestCallbacks[requestId]
        }
      }
    } else if (res.type === 'ret') {
      // topic消息
      const { topicListeners, topicRegListeners, topicRegMap } = this
      const { topic, pushType } = data
      // 原生的 atob 解码方法不支持部分 16进制字符 & emoji
      let body
      try {
        body = decode(data.body)
      } catch (error) {
        console.log(error)
        body = data.body
      }
      // 处理不含有通配符的topic
      const listenerKey = this._getListenerKey(topic, pushType)

      // 针对已经订阅主题的请求去调用
      let listenerList = topicListeners[listenerKey] || []
      for (let i = 0; i < listenerList.length; i++) {
        listenerList[i].call(this, body)
      }

      // 处理含有通配符的topic
      const regKeys = Object.keys(topicRegListeners)
      for (const regKey of regKeys) {
        listenerList = []

        if (topicRegMap[regKey]) {
          if (new RegExp('^' + topicRegMap[regKey] + '$').test(listenerKey)) {
            listenerList = topicRegListeners[regKey] || []
            for (let i = 0; i < listenerList.length; i++) {
              listenerList[i].call(this, body)
            }
          }
        }
      }
    }
  }

  /**
   * topic 主题
   * pushType 订阅 类型 MULTI GROUP SPECIAL
   * callback 订阅后触发
   * listener 主题被触发的时候触发
   */
  sub({ topic, pushType, listener, callback }) {
    this.init()
    const listenerKey = this._getListenerKey(topic, pushType)
    let listenerList
    let isReg = false
    // topic 中可能带有 +
    let tempKey = listenerKey.replace(/\+/g, '\\+')

    for (const regKey of regExpKeys) {
      if (listenerKey.indexOf(regKey) !== -1) {
        const regKeyTemp = regKey.replace(constRegexpKey, '\\$1')
        const tmp = new RegExp(regKeyTemp, 'g')
        let arr = tmp.exec(tempKey)
        // 采用循环替换替换^~^这是为了解决  2个连接在一起的问题如^~^~^
        while (arr) {
          tempKey = tempKey.replace(regKey, REGEXP_MAP[regKey])
          arr = tmp.exec(tempKey)
        }
        !isReg && (isReg = true)
      }
    }

    if (isReg) {
      listenerList = this.topicRegListeners[listenerKey] || []
      tempKey = tempKey
        .replace(/\./g, '\\.')
        .replace(/\|/g, '\\|')
        .replace(/\^(?!\^)/g, '\\^')
      this.topicRegMap[listenerKey] = tempKey
    } else {
      listenerList = this.topicListeners[listenerKey] || []
    }

    if (listenerList.length) {
      // 已经有人订阅此话题
      listenerList.push(listener)
      if (typeof callback === 'function') {
        setTimeout(() => {
          callback.call(this)
        }, 0)
      }
    } else {
      listenerList.push(listener)
      if (isReg) {
        this.topicRegListeners[listenerKey] = listenerList
      } else {
        this.topicListeners[listenerKey] = listenerList
      }
      if (this.isReady) {
        this._postSubRequest(topic, pushType, callback)
      }
    }
  }

  // 取消订阅主题
  unsub({ topic, pushType, listener, callback }) {
    const regExpKeys = Object.keys(REGEXP_MAP)
    let listenerList
    const listenerKey = this._getListenerKey(topic, pushType)
    let isReg = false

    for (const regKey of regExpKeys) {
      if (listenerKey.indexOf(regKey) !== -1 && !isReg) {
        isReg = true
        break
      }
    }

    if (isReg) {
      listenerList = this.topicRegListeners[listenerKey] || []
    } else {
      listenerList = this.topicListeners[listenerKey] || []
    }

    if (listenerList && listenerList.length > 0) {
      if (!listener) {
        // 全部取消sub
        listenerList.length = 0
      } else {
        // 取消当前listener
        for (let i = 0; i < listenerList.length; i++) {
          if (listenerList[i] === listener) {
            listenerList.splice(i, 1)
            break
          }
        }
      }
      if (listenerList.length === 0) {
        // 没有人再订阅这个topic了
        if (isReg) {
          delete this.topicRegListeners[listenerKey]
        } else {
          delete this.topicListeners[listenerKey]
        }

        if (this.isReady) {
          this._postUnsubRequest(topic, pushType, callback)
        }
        return
      }
    } else {
      if (this.isReady) {
        this._postUnsubRequest(topic, pushType, callback)
      }
    }
    if (typeof callback === 'function') {
      setTimeout(() => {
        callback.call(this)
      }, 0)
    }
  }
}
